import React, { useState } from "react";
import axios from "axios";
import {
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  Circle,
  Info,
  ShieldCheck,
  Check,
} from "lucide-react";
import { API_URL } from "../../api/api";
import { T } from "../../components/em/tokens";
import { SettingsCard } from "./settingsUi";

const PwdInput = ({ value, onChange, show, onToggle, placeholder, error }) => (
  <div style={{ position: "relative" }}>
    <input
      type={show ? "text" : "password"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: "11px 40px 11px 14px",
        border: `1px solid ${error ? T.danger : T.border}`,
        borderRadius: 10,
        font: `400 14px ${T.font}`,
        color: T.fg1,
        outline: "none",
        boxSizing: "border-box",
        background: "var(--em-bg-card)",
        transition: "border-color 150ms",
      }}
      onFocus={(e) => (e.target.style.borderColor = error ? T.danger : T.orange)}
      onBlur={(e) => (e.target.style.borderColor = error ? T.danger : T.border)}
    />
    <button
      onClick={onToggle}
      type="button"
      style={{
        position: "absolute",
        right: 12,
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: 0,
        cursor: "pointer",
        color: T.fg7,
        padding: 2,
        display: "flex",
        alignItems: "center",
      }}
    >
      {show ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  </div>
);

const PasswordManagement = () => {
  const [current, setCurrent] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState("");

  const token = localStorage.getItem("token");

  const checks = [
    { label: "At least 8 characters", pass: newPwd.length >= 8 },
    { label: "One uppercase letter (A-Z)", pass: /[A-Z]/.test(newPwd) },
    { label: "One lowercase letter (a-z)", pass: /[a-z]/.test(newPwd) },
    { label: "One number (0-9)", pass: /[0-9]/.test(newPwd) },
  ];
  const allPass = checks.every((c) => c.pass);
  const confirmMatch = newPwd && confirm && newPwd === confirm;
  const canSubmit = current && allPass && confirmMatch;

  const handleSubmit = async () => {
    setSubmitted(true);
    setServerError("");
    if (!canSubmit) return;
    try {
      await axios.put(
        `${API_URL}/api/users/change-password`,
        {
          currentPassword: current,
          newPassword: newPwd,
          confirmPassword: confirm,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      setCurrent("");
      setNewPwd("");
      setConfirm("");
      setSubmitted(false);
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setServerError(err.response?.data?.error || "Error updating password");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SettingsCard
        icon={Lock}
        title="Change Password"
        subtitle="Enter your current password and create a new strong password"
        bodyPadding="24px 24px 20px"
      >
        {success && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: 10,
              background: T.success50,
              border: "1px solid #bbf7d0",
              font: `500 13px ${T.font}`,
              color: "#00a97a",
              marginBottom: 18,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <CheckCircle2 size={16} />
            Password updated successfully!
          </div>
        )}
        {serverError && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: 10,
              background: T.danger50,
              border: "1px solid #fecaca",
              font: `500 13px ${T.font}`,
              color: T.danger,
              marginBottom: 18,
            }}
          >
            {serverError}
          </div>
        )}

        <div style={{ marginBottom: 18 }}>
          <div style={{ font: `500 13px ${T.font}`, color: T.fg1, marginBottom: 8 }}>
            Current Password
          </div>
          <PwdInput
            value={current}
            onChange={(v) => {
              setCurrent(v);
              setServerError("");
            }}
            show={showCurrent}
            onToggle={() => setShowCurrent((s) => !s)}
            placeholder="Current Password"
            error={submitted && !current}
          />
          {submitted && !current && (
            <div style={{ font: `400 11px ${T.font}`, color: T.danger, marginTop: 4 }}>
              Current password is required
            </div>
          )}
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ font: `500 13px ${T.font}`, color: T.fg1, marginBottom: 8 }}>
            New Password
          </div>
          <PwdInput
            value={newPwd}
            onChange={(v) => {
              setNewPwd(v);
              setSuccess(false);
            }}
            show={showNew}
            onToggle={() => setShowNew((s) => !s)}
            placeholder="New Password"
            error={submitted && !allPass}
          />
          {(newPwd || submitted) && (
            <div
              style={{
                marginTop: 12,
                padding: "12px 14px",
                borderRadius: 10,
                background: "#fafafa",
                border: `1px solid ${T.divider}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  font: `600 12px ${T.font}`,
                  color: T.fg4,
                  marginBottom: 8,
                }}
              >
                <Info size={13} />
                Password Requirements
              </div>
              {checks.map((c, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "3px 0",
                    font: `400 12px ${T.font}`,
                    color: c.pass ? "#00a97a" : T.fg5,
                  }}
                >
                  {c.pass ? (
                    <CheckCircle2 size={13} style={{ flexShrink: 0 }} />
                  ) : (
                    <Circle size={13} style={{ flexShrink: 0 }} />
                  )}
                  {c.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ font: `500 13px ${T.font}`, color: T.fg1, marginBottom: 8 }}>
            Confirm New Password
          </div>
          <PwdInput
            value={confirm}
            onChange={setConfirm}
            show={showConfirm}
            onToggle={() => setShowConfirm((s) => !s)}
            placeholder="Confirm New Password"
            error={submitted && confirm && !confirmMatch}
          />
          {confirm && newPwd && !confirmMatch && (
            <div style={{ font: `400 11px ${T.font}`, color: T.danger, marginTop: 4 }}>
              Passwords do not match
            </div>
          )}
          {confirm && confirmMatch && (
            <div
              style={{
                font: `400 11px ${T.font}`,
                color: "#00a97a",
                marginTop: 4,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <CheckCircle2 size={12} />
              Passwords match
            </div>
          )}
        </div>

        <button
          onClick={handleSubmit}
          style={{
            width: "100%",
            padding: "14px 20px",
            borderRadius: 10,
            border: 0,
            cursor: canSubmit ? "pointer" : "default",
            background: canSubmit ? T.gradient : T.bgMuted,
            color: canSubmit ? "#fff" : T.fg7,
            font: `700 15px ${T.font}`,
            letterSpacing: "0.01em",
            boxShadow: canSubmit ? T.shadowBrand : "none",
            transition: "all 200ms ease",
          }}
        >
          Update Password
        </button>
      </SettingsCard>

      {/* Security Best Practices card */}
      <div
        style={{
          background: "linear-gradient(135deg,#eff6ff,#f0f9ff)",
          border: "1px solid #bfdbfe",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: T.shadowSm,
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid rgba(191,219,254,0.6)",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: "rgba(43,127,255,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ShieldCheck size={20} color={T.info} />
          </div>
          <div>
            <div style={{ font: `700 18px ${T.font}`, color: T.fg1, letterSpacing: "-0.02em" }}>
              Security Best Practices
            </div>
            <div style={{ font: `400 13px ${T.font}`, color: T.fg4 }}>
              Follow these guidelines to keep your account secure
            </div>
          </div>
        </div>
        <div style={{ padding: "20px 24px" }}>
          {[
            "Use a unique password that you don't use for other accounts",
            "Consider using a password manager to generate and store strong passwords",
            "Change your password regularly (every 3-6 months recommended)",
            "Never share your password with anyone, including support staff",
            "Enable two-factor authentication when available for added security",
          ].map((tip, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "8px 0",
                borderTop: i > 0 ? "1px solid rgba(191,219,254,0.4)" : "none",
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 9999,
                  background: "rgba(43,127,255,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                <Check size={11} color={T.info} />
              </div>
              <span style={{ font: `400 13px/1.5 ${T.font}`, color: "#1e3a5f" }}>{tip}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PasswordManagement;
